/**
 * RGB color components
 */
export interface RGBColor {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse comma-separated RGB string to components
 * @param value - Color in "R,G,B" format
 * @returns RGB components or null if invalid
 */
export function parseRGBString(value: string): RGBColor | null {
  if (!value || value.trim() === "") return null;

  const parts = value.split(",").map((v) => parseInt(v.trim(), 10));
  if (parts.length !== 3 || parts.some((n) => isNaN(n))) return null;

  return { r: parts[0]!, g: parts[1]!, b: parts[2]! };
}

/**
 * Convert RGB components to comma-separated string
 */
export function toRGBString(color: RGBColor): string {
  return `${color.r},${color.g},${color.b}`;
}

/**
 * Convert RGB components to CSS rgb() format
 */
export function toRGBCss(color: RGBColor): string {
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

/**
 * Parse CSS rgb() string to components
 */
export function parseRGBCss(value: string): RGBColor | null {
  const match = value.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return null;

  return {
    r: parseInt(match[1]!, 10),
    g: parseInt(match[2]!, 10),
    b: parseInt(match[3]!, 10),
  };
}

/**
 * Convert comma-separated RGB to CSS format
 */
export function rgbStringToCss(value: string): string {
  const color = parseRGBString(value);
  return color ? toRGBCss(color) : "";
}

/**
 * Convert CSS rgb() to comma-separated format
 */
export function rgbCssToString(value: string): string {
  const color = parseRGBCss(value);
  return color ? toRGBString(color) : "";
}
